import { FinancialReport } from '@/repositories/ReportRepository'
import {
  Box,
  Container,
  Heading,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useOutletContext } from 'react-router-dom'
import { OwnerReportTable } from '../../components/features/reports/OwnerReportTable'
import {
  exportOwnerReportToPDF,
  exportReportToPDF,
} from '../../utils/pdf-export'

type PayoutReportsContext = {
  startDate: Date | null
  endDate: Date | null
  payoutData: FinancialReport[]
  loading: boolean
}

export function PayoutReportsPage() {
  const { startDate, endDate, payoutData, loading } =
    useOutletContext<PayoutReportsContext>()

  const toast = useToast()

  const handleExportPDF = () => {
    try {
      if (payoutData.length === 0) {
        toast({
          title: 'Nenhum dado para exportar',
          status: 'warning',
          duration: 3000,
        })
        return
      }
      const fileName = exportReportToPDF(payoutData, startDate, endDate)
      toast({
        title: 'PDF gerado com sucesso!',
        description: `Arquivo ${fileName} foi baixado.`,
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      console.error(error)
      toast({
        title: 'Erro ao gerar PDF',
        description:
          error instanceof Error ? error.message : 'Erro desconhecido',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleExportOwnerPDF = async (ownerId: string) => {
    try {
      const ownerData = payoutData.find((item) => item.owner_id === ownerId)
      if (!ownerData) return
      const fileName = await exportOwnerReportToPDF(
        ownerData,
        startDate,
        endDate
      )
      toast({
        title: 'PDF gerado com sucesso!',
        description: `Arquivo ${fileName} foi baixado.`,
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      console.error(error)
      toast({
        title: 'Erro ao gerar PDF',
        description:
          error instanceof Error ? error.message : 'Erro desconhecido',
        status: 'error',
        duration: 5000,
      })
    }
  }

  if (loading && payoutData.length === 0) {
    return (
      <Container maxW="container.xl" py={{ base: 4, md: 6 }}>
        <VStack spacing={8} align="center" justify="center" minH="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">
            Carregando dados de repasse...
          </Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Box p={{ base: 2, md: 6 }}>
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        <Box>
          <Heading
            as="h1"
            size={{ base: 'lg', md: 'xl' }}
            color="gray.800"
            mb={2}
          >
            Relatórios de Repasse
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
            Gestão de repasses para proprietários de imóveis
          </Text>
        </Box>

        <OwnerReportTable
          reportData={payoutData}
          loading={loading}
          onExportPDF={handleExportPDF}
          onExportOwnerPDF={handleExportOwnerPDF}
        />
      </VStack>
    </Box>
  )
}
